/* eslint-disable */
import {
  Badge,
  Box,
  Button,
  ChakraProvider,
  Divider,
  Flex,
  Heading,
  Icon,
  Image,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { MdCheckCircle, MdError } from 'react-icons/md';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useRef } from 'react';
import resguardosApi from 'api/resguardos';
import initialTheme from 'theme/theme';

type ResguardoInfo = {
  ResguardoId: number;
  usuarioDueno: string;
  usuarioNumColab: string;
  departamento: string;
  estado: string;
  activos: { Id: number; ActiveName: string; SerialNumber: string; Modelo?: string }[];
};

type PageState = 'loading' | 'ready' | 'already_signed' | 'success' | 'error';

export default function FirmaRemota() {
  const { token } = useParams<{ token: string }>();
  const toast = useToast();
  const sigCanvas = useRef<SignatureCanvas>(null);

  const [pageState, setPageState] = React.useState<PageState>('loading');
  const [resguardo, setResguardo] = React.useState<ResguardoInfo | null>(null);
  const [canvasVacio, setCanvasVacio] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');

  React.useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMsg('El enlace no es válido.');
      return;
    }
    resguardosApi
      .obtenerPorToken(token)
      .then((data: any) => {
        if (data.estado === 'Recepción confirmada') {
          setPageState('already_signed');
          return;
        }
        setResguardo(data);
        setPageState('ready');
      })
      .catch(() => {
        setPageState('error');
        setErrorMsg('El enlace no es válido o ha expirado.');
      });
  }, [token]);

  const handleFirmar = async () => {
    if (!token || !sigCanvas.current || sigCanvas.current.isEmpty()) return;
    setSubmitting(true);
    try {
      const firmaDigital = sigCanvas.current.getCanvas().toDataURL('image/png');
      await resguardosApi.confirmarFirmaRemota(token, firmaDigital);
      setPageState('success');
    } catch {
      toast({
        title: 'Error al guardar la firma',
        description: 'Ocurrió un error. Por favor intenta de nuevo.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ChakraProvider theme={initialTheme}>
      <Flex
        minH="100vh"
        bg="gray.50"
        direction="column"
        align="center"
        justify="flex-start"
        py="40px"
        px="16px"
      >
        {/* Header */}
        <Box textAlign="center" mb="32px">
          <Heading size="md" color="navy.700" mb="2">
            Velas Resorts
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Sistema de Control de Inventarios
          </Text>
        </Box>

        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="md"
          p={{ base: '24px', md: '40px' }}
          w="100%"
          maxW="600px"
        >
          {/* Estado: Cargando */}
          {pageState === 'loading' && (
            <Flex direction="column" align="center" py="40px" gap="4">
              <Spinner size="xl" color="brand.500" thickness="3px" />
              <Text color="gray.500">Cargando información del resguardo…</Text>
            </Flex>
          )}

          {/* Estado: Error */}
          {pageState === 'error' && (
            <Flex direction="column" align="center" py="40px" gap="4" textAlign="center">
              <Icon as={MdError} w="12" h="12" color="red.400" />
              <Heading size="md" color="red.500">Enlace inválido</Heading>
              <Text color="gray.500">{errorMsg}</Text>
            </Flex>
          )}

          {/* Estado: Ya fue firmado */}
          {pageState === 'already_signed' && (
            <Flex direction="column" align="center" py="40px" gap="4" textAlign="center">
              <Icon as={MdCheckCircle} w="12" h="12" color="green.400" />
              <Heading size="md" color="green.600">Resguardo ya firmado</Heading>
              <Text color="gray.500">
                Este resguardo ya fue confirmado. No es necesaria ninguna acción adicional.
              </Text>
            </Flex>
          )}

          {/* Estado: Éxito tras firmar */}
          {pageState === 'success' && (
            <Flex direction="column" align="center" py="40px" gap="4" textAlign="center">
              <Icon as={MdCheckCircle} w="12" h="12" color="green.400" />
              <Heading size="md" color="green.600">¡Firma registrada!</Heading>
              <Text color="gray.500">
                Tu firma ha sido guardada correctamente. Recibirás una copia por correo electrónico.
              </Text>
            </Flex>
          )}

          {/* Estado: Listo para firmar */}
          {pageState === 'ready' && resguardo && (
            <>
              <Flex align="center" justify="space-between" mb="4">
                <Heading size="md" color="navy.700">
                  Resguardo #{resguardo.ResguardoId}
                </Heading>
                <Badge colorScheme="purple" fontSize="0.8em" px="3" py="1" borderRadius="md">
                  Autorizado
                </Badge>
              </Flex>

              <Divider mb="4" />

              {/* Info del colaborador */}
              <Box mb="4">
                <Text fontSize="sm" color="gray.500" mb="1">Colaborador</Text>
                <Text fontWeight="semibold">{resguardo.usuarioDueno}</Text>
                <Text fontSize="sm" color="gray.400">
                  No. Colaborador: {resguardo.usuarioNumColab}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Departamento: {resguardo.departamento}
                </Text>
              </Box>

              <Divider mb="4" />

              {/* Lista de activos */}
              <Box mb="6">
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb="2">
                  Activos asignados ({resguardo.activos?.length ?? 0})
                </Text>
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Nombre</Th>
                        <Th>N° Serie</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {resguardo.activos?.map((activo, i) => (
                        <Tr key={i}>
                          <Td>
                            <Text fontWeight="medium">{activo.ActiveName}</Text>
                            {activo.Modelo && (
                              <Text fontSize="xs" color="gray.400">
                                {activo.Modelo}
                              </Text>
                            )}
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{activo.SerialNumber}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>

              {/* Canvas de firma */}
              <Box mb="4">
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb="2">
                  Firma del colaborador
                </Text>
                <Text fontSize="xs" color="gray.400" mb="3">
                  Firme en el recuadro de abajo para confirmar la recepción de los activos.
                </Text>
                <Box
                  border="1px dashed"
                  borderColor="gray.300"
                  borderRadius="md"
                  width="100%"
                  height="180px"
                  bg="white"
                  position="relative"
                  overflow="hidden"
                >
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    onBegin={() => setCanvasVacio(false)}
                    onEnd={() =>
                      setCanvasVacio(sigCanvas.current?.isEmpty() ?? true)
                    }
                    canvasProps={{
                      width: 540,
                      height: 180,
                      style: { width: '100%', height: '100%' },
                    }}
                  />
                </Box>
                <Flex justify="flex-end" mt="2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      sigCanvas.current?.clear();
                      setCanvasVacio(true);
                    }}
                  >
                    Limpiar
                  </Button>
                </Flex>
              </Box>

              <Button
                colorScheme="blue"
                w="100%"
                size="lg"
                isDisabled={canvasVacio}
                isLoading={submitting}
                loadingText="Guardando firma…"
                onClick={handleFirmar}
              >
                Confirmar y firmar
              </Button>

              <Text fontSize="xs" color="gray.400" textAlign="center" mt="3">
                Al firmar, confirmas haber recibido los activos listados arriba.
              </Text>
            </>
          )}
        </Box>
      </Flex>
    </ChakraProvider>
  );
}
